import React, { useState } from 'react';
import { Link, useLocation, useNavigate, NavLink } from 'react-router-dom';
import {
  BarChart3,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  Activity,
  TrendingUp,
  ChevronLeft,
  ScrollText,
  Tag,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const navigation = [
  { name: 'Dashboard', href: '/admin', icon: BarChart3 },
  { name: 'User management', href: '/admin/users', icon: Users },
  { name: 'Pricing plans', href: '/admin/plans', icon: Tag },
  { name: 'System health', href: '/admin/health', icon: Activity },
  { name: 'System analytics', href: '/admin/analytics', icon: TrendingUp },
  { name: 'Audit log', href: '/admin/audit-logs', icon: ScrollText },
  { name: 'System settings', href: '/admin/settings', icon: Settings },
];

const SidebarNav = ({ onItemClick }) => (
  <nav className="flex-1 space-y-0.5 px-3 py-4">
    {navigation.map((item) => {
      const Icon = item.icon;
      return (
        <NavLink
          key={item.name}
          to={item.href}
          end={item.href === '/admin'}
          onClick={onItemClick}
          className={({ isActive }) =>
            [
              'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition',
              isActive
                ? 'bg-white text-ink-900 shadow-soft'
                : 'text-ink-300 hover:bg-white/5 hover:text-white',
            ].join(' ')
          }
        >
          <Icon className="h-4 w-4 opacity-90" />
          {item.name}
        </NavLink>
      );
    })}
  </nav>
);

const SidebarHeader = ({ onClose }) => (
  <div className="flex h-16 items-center justify-between px-5 border-b border-white/10">
    <Link to="/admin" className="flex items-center gap-2.5 text-white">
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 shadow-soft">
        <Shield className="h-4 w-4" />
      </span>
      <span className="text-base font-semibold tracking-tight">Admin</span>
    </Link>
    {onClose && (
      <button
        onClick={onClose}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-300 hover:bg-white/10 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>
    )}
  </div>
);

const SidebarFooter = ({ onLogout }) => (
  <div className="border-t border-white/10 p-4 space-y-2">
    <Link
      to="/"
      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ink-300 hover:text-white hover:bg-white/5 transition"
    >
      <ChevronLeft className="h-4 w-4" />
      Back to app
    </Link>
    <button
      onClick={onLogout}
      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-rose-300 hover:text-rose-200 hover:bg-rose-500/10 transition"
    >
      <LogOut className="h-4 w-4" />
      Sign out
    </button>
  </div>
);

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentName =
    navigation
      .slice()
      .sort((a, b) => b.href.length - a.href.length)
      .find(
        (item) =>
          location.pathname === item.href ||
          location.pathname.startsWith(item.href + '/')
      )?.name || 'Admin';

  return (
    <div className="-mx-4 sm:-mx-6 lg:-mx-8 -my-8 sm:-my-10 min-h-screen bg-ink-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-ink-900/60 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-ink-900 text-white shadow-pop">
            <SidebarHeader onClose={() => setSidebarOpen(false)} />
            <SidebarNav onItemClick={() => setSidebarOpen(false)} />
            <SidebarFooter onLogout={handleLogout} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-ink-900 text-white">
          <SidebarHeader />
          <SidebarNav />
          <SidebarFooter onLogout={handleLogout} />
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 items-center gap-x-4 border-b border-ink-200 bg-white/80 backdrop-blur-md px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg border border-ink-200 bg-white text-ink-700 hover:bg-ink-50"
          >
            <Menu className="h-4 w-4" />
          </button>

          <div className="flex flex-1 items-center justify-between">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-ink-500">
                Admin
              </p>
              <h1 className="text-base font-semibold text-ink-900 tracking-tight">
                {currentName}
              </h1>
            </div>
            <div className="hidden sm:inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700">
              <Shield className="h-3.5 w-3.5" />
              Admin access
            </div>
          </div>
        </div>

        <main className="py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
